// The node field sets, carried over from PanelDrawer's Details tab, TraitsTab's DetailDrawer and
// SNPDetailDrawer — plus, since A5, the gene node's own set: a wheel_genes row is a real record now,
// so rename/registry-link/ring-position edit it directly (an id-LESS gene still has no editor).
import React from "react";
import { Button, Checkbox, Form, Input, InputNumber, Select } from "antd";
import { DownOutlined, LinkOutlined, LockOutlined, UpOutlined } from "@ant-design/icons";

import GeneSymbolField from "./GeneSymbolField";
import SnpGsaReference from "./SnpGsaReference";
import SnpRegistryReference from "./SnpRegistryReference";
import { RegistryGene } from "../../../../types/geneCodes/RegistryTypes";

const { TextArea } = Input;

const modalityOptions = [
	{ label: "Genetic", value: "genetic" },
	{ label: "Lifestyle", value: "lifestyle" },
	{ label: "Nutrition", value: "nutrition" },
	{ label: "Environmental", value: "environmental" },
];

const sexOptions = [
	{ label: "Male", value: "male" },
	{ label: "Female", value: "female" },
	{ label: "Both", value: "both" },
];

const qualityOptions = [
	{ label: "0 - Low", value: 0 },
	{ label: "1 - High", value: 1 },
];

const markerTypeOptions = [
	{ label: "SNP", value: "SNP" },
	{ label: "INDEL", value: "INDEL" },
	{ label: "CNV", value: "CNV" },
	{ label: "Other", value: "Other" },
];

const arrayTypeOptions = [
	{ label: "GSA", value: "GSA" },
	{ label: "Omni", value: "Omni" },
	{ label: "Custom", value: "Custom" },
];

const actionableOptions = [
	{ label: "Yes", value: "yes" },
	{ label: "No", value: "no" },
];

interface LockedFieldProps {
	label: string;
	value: string;
	hint: string;
	testId: string;
}

// A read-only echo of the node's position, not a form field — the wheel decides it, so it is never posted.
export const LockedField: React.FC<LockedFieldProps> = ({ label, value, hint, testId }) => (
	<div className="mg-node-editor__locked">
		<span className="mg-node-editor__label">{label}</span>
		<div className="mg-node-editor__locked-value" data-testid={testId}>
			<LockOutlined />
			<span>{value}</span>
		</div>
		<span className="mg-node-editor__hint">{hint}</span>
	</div>
);

export const PanelFields: React.FC = () => (
	<>
		<Form.Item label="Wheel name" name="name_panel" rules={[{ required: true, message: "Please enter a wheel name" }]}>
			<Input placeholder="Enter wheel name" />
		</Form.Item>
		<Form.Item label="Short description" name="desc_short">
			<Input placeholder="Enter short description" />
		</Form.Item>
		<Form.Item label="Long description" name="desc_long">
			<TextArea rows={4} placeholder="Enter detailed description" />
		</Form.Item>
		<div className="mg-node-editor__grid">
			<Form.Item label="Modality" name="modality">
				<Select placeholder="Select modality" options={modalityOptions} allowClear />
			</Form.Item>
			<Form.Item label="Sex" name="panel_sex">
				<Select placeholder="Select sex" options={sexOptions} allowClear />
			</Form.Item>
			<Form.Item label="Red tag" name="red_tag">
				<Input placeholder="Enter red tag" />
			</Form.Item>
			<Form.Item label="Green tag" name="green_tag">
				<Input placeholder="Enter green tag" />
			</Form.Item>
		</div>
	</>
);

export const TraitFields: React.FC = () => (
	<>
		<Form.Item label="Trait name" name="trait_name" rules={[{ required: true, message: "Please enter a trait name" }]}>
			<Input placeholder="Enter trait name" />
		</Form.Item>
		<Form.Item label="Description" name="trait_desc">
			<TextArea rows={3} placeholder="Enter description" />
		</Form.Item>
		<div className="mg-node-editor__grid">
			<Form.Item label="Lifetime risk" name="lifetime_risk">
				<Input placeholder="e.g. 1 in 3 women over 50" />
			</Form.Item>
			<Form.Item label="Searcher term" name="trait_searcher" extra="Used to match literature and catalogue results.">
				<Input placeholder="Enter searcher term" />
			</Form.Item>
		</div>
		<Form.Item label="Treatment information" name="trait_treatment">
			<TextArea rows={3} placeholder="Enter treatment information" />
		</Form.Item>
	</>
);

interface SnpFieldsProps {
	creating: boolean;
}

// `rs` and `risk_allele` are required only when creating, which is exactly where the server requires
// them (StagingController::store); update leaves both nullable, so a legacy row stays saveable.
export const SnpFields: React.FC<SnpFieldsProps> = ({ creating }) => (
	<>
		<div className="mg-node-editor__grid">
			<Form.Item label="RS ID" name="rs" rules={[{ required: creating, message: "Please enter an RS ID" }]}>
				<Input placeholder="e.g. rs1234567" />
			</Form.Item>
			<Form.Item label="Gene" name="gene">
				<GeneSymbolField placeholder="e.g. VDR" />
			</Form.Item>
			<Form.Item label="Risk allele" name="risk_allele" rules={[{ required: creating, message: "Please enter a risk allele" }]}>
				<Input placeholder="e.g. A" />
			</Form.Item>
			<Form.Item label="Benefit allele" name="bene_allele">
				<Input placeholder="e.g. G" />
			</Form.Item>
			<Form.Item label="Quality" name="quality">
				<Select placeholder="Select quality" options={qualityOptions} allowClear />
			</Form.Item>
			<Form.Item label="Rank" name="rank">
				<InputNumber placeholder="Enter rank" min={0} className="mg-node-editor__number" />
			</Form.Item>
			<Form.Item label="Variant alias" name="var_alias">
				<Input placeholder="e.g. c.123A>G" />
			</Form.Item>
			<Form.Item label="Taqman ID" name="taqman">
				<Input placeholder="e.g. C_12345678_10" />
			</Form.Item>
			<Form.Item label="Marker type" name="marker_type">
				<Select placeholder="Select marker type" options={markerTypeOptions} allowClear />
			</Form.Item>
			<Form.Item label="Array type" name="array_type">
				<Select placeholder="Select array type" options={arrayTypeOptions} allowClear />
			</Form.Item>
			<Form.Item label="Client" name="client">
				<Input placeholder="Client name" />
			</Form.Item>
			<Form.Item label="Actionable" name="actionable">
				<Select placeholder="Is actionable?" options={actionableOptions} allowClear />
			</Form.Item>
			<Form.Item label="Endophenotype" name="endophenotype">
				<Input />
			</Form.Item>
			<Form.Item label="Heterozygous coefficient" name="hz_coef">
				<InputNumber min={0} max={1} step={0.01} className="mg-node-editor__number" />
			</Form.Item>
		</div>
		<Form.Item label="Modifiable" name="modifiable" valuePropName="checked">
			<Checkbox>Modifiable</Checkbox>
		</Form.Item>
		<Form.Item label="Notes" name="notes">
			<TextArea rows={3} />
		</Form.Item>
		<SnpGsaReference />
		<SnpRegistryReference />
	</>
);

interface GeneFieldsProps {
	registryRef: string | null;
	onPickGene: (gene: RegistryGene) => void;
	/** 0-based position within the trait's ring, held by the editor until save. */
	position: number;
	ringSize: number;
	onMove: (delta: -1 | 1) => void;
	/** R6: a gene being created is not in the ring yet, so there is no position to move it to. */
	creating?: boolean;
}

// The A5 gene node set. The symbol field is the B6 autocomplete; picking a registry match is what
// sets registry_ref, so the link can never name a doc the author did not see.
export const GeneFields: React.FC<GeneFieldsProps> = ({ registryRef, onPickGene, position, ringSize, onMove, creating = false }) => (
	<>
		<Form.Item label="Gene symbol" name="gene_symbol" rules={[{ required: true, message: "Please enter a gene symbol" }]}>
			<GeneSymbolField placeholder="e.g. SERPINE1" onPickGene={onPickGene} />
		</Form.Item>
		<div className="mg-node-editor__grid">
			<div className="mg-node-editor__locked">
				<span className="mg-node-editor__label">Registry link</span>
				<div className="mg-node-editor__locked-value" data-testid="node-editor-registry-link">
					<LinkOutlined />
					<span>{registryRef ?? "Not linked"}</span>
				</div>
				<span className="mg-node-editor__hint">Set by picking a registry match in the symbol field.</span>
			</div>
			{creating ? (
				<div className="mg-node-editor__locked">
					<span className="mg-node-editor__label">Ring position</span>
					<div className="mg-node-editor__locked-value" data-testid="node-editor-position-new">
						<LockOutlined />
						<span>Last in the ring</span>
					</div>
					<span className="mg-node-editor__hint">Reorder it once it is on the wheel.</span>
				</div>
			) : (
				<div className="mg-node-editor__locked">
					<span className="mg-node-editor__label">Ring position</span>
					<div className="mg-node-editor__position" data-testid="node-editor-position">
						<Button size="small" icon={<UpOutlined />} disabled={position <= 0} onClick={() => onMove(-1)} aria-label="Move earlier in the ring" data-testid="node-editor-position-up" />
						<span data-testid="node-editor-position-value">
							{position + 1} of {ringSize}
						</span>
						<Button size="small" icon={<DownOutlined />} disabled={position >= ringSize - 1} onClick={() => onMove(1)} aria-label="Move later in the ring" data-testid="node-editor-position-down" />
					</div>
					<span className="mg-node-editor__hint">Applied to the whole ring on save.</span>
				</div>
			)}
		</div>
	</>
);
